import { useQuery } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import type { CustomerField } from "~/types/api";

const getCustomerFieldsQueryKey = (customerId: number) =>
  configurationApi.queryOptions("get", "/api/Customer/{customerId}/field", {
    params: { path: { customerId } },
  }).queryKey;

export const useCustomerFields = (customerId: number | undefined, enabled = true) => {
  const { showToast } = useToast();
  const { data, isLoading, error, refetch } = useQuery<CustomerField[]>({
    queryKey: customerId ? getCustomerFieldsQueryKey(customerId) : ["customer-fields", "none"],
    queryFn: async () => {
      if (!customerId) return [];

      const response = await configurationApi.client.GET("/api/Customer/{customerId}/field", {
        params: {
          path: {
            customerId,
          },
        },
      });

      if (response.error) {
        const errorMessage = `Failed to fetch customer fields: ${response.error.toString()}`;
        showToast("error", "Error", errorMessage);
        throw new Error(errorMessage);
      }

      return response.data || [];
    },
    // A filter to exclude "Brand", since this was introduced on all Customers because of the old grouping logic.
    select: (fields) => fields.filter((field) => field.name.toLowerCase() !== "brand"),
    enabled: enabled && !!customerId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    fields: data || [],
    isLoading,
    error,
    refetch,
  };
};
